import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { FaChevronDown, FaChevronRight, FaFileInvoice } from "react-icons/fa";
import { IoClose } from "react-icons/io5";
import SetupTittle from "../../../setupPage/SharedComponents/SetupTittle";
import SetupContainer from "../../../setupPage/SharedComponents/SetupContainer";
import Box from "../../../setupPage/SharedComponents/Box";
import PaymentSlip from "../../../bookings/components/Payment/PaymentSlip";
import { buildAdminPaymentSlipData } from "./buildAdminPaymentSlipData";
import { formatCurrency } from "../../../../utils/formatCurrency";
import useParcelBookingRefresh from "../../../../hooks/useParcelBookingRefresh";

const ITEMS_PER_PAGE = 10;

const getAuthHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const formatDateTime = (value) => {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
};

const sumParcelSubtotal = (parcel) =>
  (parcel.details || []).reduce(
    (sum, d) => sum + (Number(d.subTotal) || 0),
    0,
  );

const formatStripeCard = (parcel) => {
  const brand = (parcel.cardBrand || "").trim();
  const last4 = (parcel.cardLast4 || "").trim();
  if (brand && last4) return `${brand} •••• ${last4}`;
  if (last4) return `•••• ${last4}`;
  if (brand) return brand;
  if (parcel.stripePaymentIntentId) return "Card (details pending)";
  return null;
};

const sanitizePaymentStatusLabel = (name) =>
  String(name ?? "")
    .replace(/[\u200B-\u200D\uFEFF]/g, "")
    .trim();

const isValidPaymentStatusName = (name) => {
  const label = sanitizePaymentStatusLabel(name);
  if (!label) return false;
  const lower = label.toLowerCase();
  return lower !== "null" && lower !== "undefined";
};

const normalizePaymentStatusKey = (name) =>
  sanitizePaymentStatusLabel(name).toLowerCase();

const getDetailPaymentStatus = (row) => {
  if (!row || typeof row !== "object") return "";
  const raw =
    row.paymentStatus ?? row.paymentStatusName ?? row.statusName ?? "";
  return isValidPaymentStatusName(raw) ? sanitizePaymentStatusLabel(raw) : "";
};

const getParcelPaymentStatuses = (parcel) => {
  const statuses = new Set();
  const parcelLevel = getDetailPaymentStatus(parcel);
  if (parcelLevel) statuses.add(parcelLevel);
  (parcel?.details || []).forEach((d) => {
    const status = getDetailPaymentStatus(d);
    if (status) statuses.add(status);
  });
  return Array.from(statuses);
};

const resolvePaymentStatusFilterKey = (filter, statusOptions = []) => {
  if (!filter || filter === "all") return "";

  if (filter.startsWith("name:")) {
    return filter.slice(5);
  }

  const asId = parseInt(filter, 10);
  if (Number.isFinite(asId) && String(asId) === String(filter).trim()) {
    const fromApi = statusOptions.find(
      (s) => parseInt(s.id, 10) === asId,
    );
    if (fromApi?.statusName) {
      return normalizePaymentStatusKey(fromApi.statusName);
    }
  }

  return normalizePaymentStatusKey(filter);
};

const parcelMatchesPaymentStatus = (
  parcel,
  paymentStatusFilter,
  statusOptions = [],
) => {
  const filterKey = resolvePaymentStatusFilterKey(
    paymentStatusFilter,
    statusOptions,
  );
  if (!filterKey) return true;

  if (
    getParcelPaymentStatuses(parcel).some(
      (status) => normalizePaymentStatusKey(status) === filterKey,
    )
  ) {
    return true;
  }

  const filterId = parseInt(paymentStatusFilter, 10);
  if (!Number.isFinite(filterId)) return false;

  if (
    parcel.paymentStatusId != null &&
    parseInt(parcel.paymentStatusId, 10) === filterId
  ) {
    return true;
  }

  return (parcel.details || []).some(
    (d) =>
      d.paymentStatusId != null &&
      parseInt(d.paymentStatusId, 10) === filterId,
  );
};

const parcelMatchesSearch = (parcel, term) => {
  if (!term) return true;
  const t = term.toLowerCase();
  const parentValues = [
    parcel.parcelNo,
    parcel.parcelStatusName,
    parcel.senderName,
    parcel.senderEmail,
    parcel.senderPhone,
    parcel.senderPostCode,
    parcel.receiverName,
    parcel.receiverEmail,
    parcel.receiverPhone,
    parcel.receiverPostCode,
    parcel.stripePaymentIntentId,
    parcel.stripePaymentMethod,
    parcel.stripeChargeId,
    parcel.cardBrand,
    parcel.cardLast4,
  ];
  if (parentValues.some((v) => v != null && String(v).toLowerCase().includes(t))) {
    return true;
  }
  return (parcel.details || []).some((d) =>
    Object.values(d).some(
      (v) => v != null && String(v).toLowerCase().includes(t),
    ),
  );
};

const statusBadgeClass = (status) => {
  const s = (status || "").toLowerCase();
  if (s.includes("paid")) return "bg-emerald-100 text-emerald-800";
  if (s.includes("ready")) return "bg-blue-100 text-blue-800";
  if (s.includes("deliver")) return "bg-green-100 text-green-800";
  if (s.includes("pending") || s.includes("hold")) return "bg-yellow-100 text-yellow-800";
  if (s.includes("cancel")) return "bg-red-100 text-red-800";
  return "bg-gray-100 text-gray-800";
};

const AdminBookings = () => {
  const backendUrl = process.env.REACT_APP_API_URL;
  const [parcels, setParcels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [paymentStatusFilter, setPaymentStatusFilter] = useState("all");
  const [paymentStatusOptions, setPaymentStatusOptions] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [expandedIds, setExpandedIds] = useState({});
  const [slipParcel, setSlipParcel] = useState(null);
  const [slipPaymentData, setSlipPaymentData] = useState(null);
  const [slipLoading, setSlipLoading] = useState(false);

  const fetchParcelHistory = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${backendUrl}ParcelBookingInformation/GetParcelHistory`,
        { headers: { accept: "*/*", ...getAuthHeaders() } },
      );
      const data = Array.isArray(res.data) ? res.data : [];
      setParcels(data);
    } catch (err) {
      const message =
        err?.response?.data?.message ||
        err?.message ||
        "Failed to load booking history.";
      toast.error(message);
      setParcels([]);
    } finally {
      setLoading(false);
    }
  }, [backendUrl]);

  useEffect(() => {
    fetchParcelHistory();
  }, [fetchParcelHistory]);

  useParcelBookingRefresh(fetchParcelHistory);

  useEffect(() => {
    axios
      .get(`${backendUrl}Payment/GetAllPaymentStatus`, {
        headers: { accept: "*/*", ...getAuthHeaders() },
      })
      .then((res) => {
        setPaymentStatusOptions(Array.isArray(res.data) ? res.data : []);
      })
      .catch(() => setPaymentStatusOptions([]));
  }, [backendUrl]);

  const paymentStatusFilterOptions = useMemo(() => {
    const byName = new Map();

    const addOption = (name) => {
      if (!isValidPaymentStatusName(name)) return;
      const label = sanitizePaymentStatusLabel(name);
      const key = normalizePaymentStatusKey(label);
      if (!key || byName.has(key)) return;
      byName.set(key, { value: key, label });
    };

    paymentStatusOptions.forEach((status) => {
      addOption(status.statusName ?? status.name);
    });

    parcels.forEach((parcel) => {
      getParcelPaymentStatuses(parcel).forEach((name) => addOption(name));
    });

    return Array.from(byName.values())
      .filter((opt) => opt.value && opt.label)
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [paymentStatusOptions, parcels]);

  useEffect(() => {
    if (paymentStatusFilter === "all") return;
    const isValid = paymentStatusFilterOptions.some(
      (opt) => opt.value === paymentStatusFilter,
    );
    if (!isValid) setPaymentStatusFilter("all");
  }, [paymentStatusFilterOptions, paymentStatusFilter]);

  const filteredParcels = useMemo(
    () =>
      parcels.filter(
        (p) =>
          parcelMatchesSearch(p, searchText.trim()) &&
          parcelMatchesPaymentStatus(
            p,
            paymentStatusFilter,
            paymentStatusOptions,
          ),
      ),
    [parcels, searchText, paymentStatusFilter, paymentStatusOptions],
  );

  const totalPages = Math.max(1, Math.ceil(filteredParcels.length / ITEMS_PER_PAGE));

  const paginatedParcels = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredParcels.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredParcels, currentPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchText, paymentStatusFilter]);

  const clearFilters = () => {
    setSearchText("");
    setPaymentStatusFilter("all");
  };

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const toggleExpanded = (parcelNo) => {
    setExpandedIds((prev) => ({
      ...prev,
      [parcelNo]: !prev[parcelNo],
    }));
  };

  const expandAllOnPage = () => {
    const next = { ...expandedIds };
    paginatedParcels.forEach((p) => {
      next[p.parcelNo] = true;
    });
    setExpandedIds(next);
  };

  const collapseAll = () => {
    setExpandedIds({});
  };

  const closePaymentSlip = () => {
    setSlipParcel(null);
    setSlipPaymentData(null);
  };

  const openPaymentSlip = async (parcel) => {
    setSlipParcel(parcel);
    setSlipPaymentData(null);
    setSlipLoading(true);
    try {
      let costData = {};
      try {
        const costRes = await axios.get(
          `${backendUrl}ParcelDetails/GetParcelCostDetails?parcelNo=${parcel.parcelNo}`,
          { headers: { accept: "*/*", ...getAuthHeaders() } },
        );
        costData = costRes.data || {};
      } catch {
        toast.warn(
          "Cost details unavailable; showing booking summary from history.",
        );
      }
      setSlipPaymentData(buildAdminPaymentSlipData(parcel, costData));
    } catch (err) {
      const message =
        err?.response?.data?.message ||
        err?.message ||
        "Failed to load payment slip.";
      toast.error(message);
      closePaymentSlip();
    } finally {
      setSlipLoading(false);
    }
  };

  const pageNumbers = useMemo(() => {
    const pages = [];
    const maxButtons = 7;
    if (totalPages <= maxButtons) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
      return pages;
    }
    let start = Math.max(1, currentPage - 2);
    let end = Math.min(totalPages, start + maxButtons - 1);
    start = Math.max(1, end - maxButtons + 1);
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  }, [currentPage, totalPages]);

  return (
    <SetupContainer>
      <SetupTittle text="Bookings" />

      <Box>
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
          <p className="text-gray-600">
            View parcel bookings, open payment slips, and line-item details.
          </p>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={fetchParcelHistory}
              disabled={loading}
              className="px-4 py-2 rounded-lg bg-[#6D4C41] text-white font-medium hover:opacity-90 disabled:opacity-60"
            >
              {loading ? "Refreshing..." : "Refresh"}
            </button>
            <button
              type="button"
              onClick={expandAllOnPage}
              className="px-4 py-2 rounded-lg border border-gray-300 bg-white font-medium hover:bg-gray-50"
            >
              Expand page
            </button>
            <button
              type="button"
              onClick={collapseAll}
              className="px-4 py-2 rounded-lg border border-gray-300 bg-white font-medium hover:bg-gray-50"
            >
              Collapse all
            </button>
          </div>
        </div>

        <div className="flex flex-col lg:flex-row gap-3 mb-6">
          <input
            type="text"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="Search parcel no, names, emails, route, status..."
            className="flex-1 border-2 border-[#83430D] h-12 px-4 outline-none rounded-lg bg-white"
          />
          <select
            value={paymentStatusFilter}
            onChange={(e) => setPaymentStatusFilter(e.target.value)}
            className="border-2 border-[#83430D] h-12 px-4 outline-none rounded-lg bg-white min-w-[220px] font-medium text-gray-800"
            aria-label="Filter by payment status"
          >
            <option value="all">All payment statuses</option>
            {paymentStatusFilterOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={clearFilters}
            disabled={!searchText && paymentStatusFilter === "all"}
            className="px-5 py-2 rounded-lg border border-gray-300 bg-white font-medium hover:bg-gray-50 disabled:opacity-50"
          >
            Clear filters
          </button>
        </div>

        {loading && parcels.length === 0 ? (
          <p className="text-center py-12 font-medium text-gray-600">
            Loading bookings...
          </p>
        ) : filteredParcels.length === 0 ? (
          <p className="text-center py-12 font-medium text-gray-600">
            No bookings found.
          </p>
        ) : (
          <div className="space-y-4">
            {paginatedParcels.map((parcel, index) => {
              const isOpen = !!expandedIds[parcel.parcelNo];
              const details = parcel.details || [];
              const totalSubtotal = sumParcelSubtotal(parcel);
              const slipSymbol = details[0]?.symbol;
              const stripeCardLabel = formatStripeCard(parcel);
              const paymentStatuses = getParcelPaymentStatuses(parcel);
              const globalIndex =
                (currentPage - 1) * ITEMS_PER_PAGE + index + 1;

              return (
                <div
                  key={parcel.parcelNo}
                  className="rounded-xl border border-[#e8d5c8] bg-[#fcf4ef] overflow-hidden shadow-sm"
                >
                  <div className="flex flex-col sm:flex-row sm:items-stretch">
                    <button
                      type="button"
                      onClick={() => toggleExpanded(parcel.parcelNo)}
                      className="flex-1 text-left px-4 py-4 sm:px-5 flex items-start gap-3 hover:bg-[#fceee5] transition-colors"
                    >
                      <span className="mt-1 text-[#6D4C41] shrink-0">
                        {isOpen ? <FaChevronDown /> : <FaChevronRight />}
                      </span>
                      <div className="flex-1 min-w-0 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                        <div>
                          <p className="text-xs font-semibold text-gray-500 uppercase">
                            Parcel
                          </p>
                          <p className="font-bold text-lg text-gray-900">
                            #{parcel.parcelNo}
                          </p>
                          <span
                            className={`inline-block mt-1 px-2 py-0.5 text-xs font-semibold rounded-full ${statusBadgeClass(parcel.parcelStatusName)}`}
                          >
                            {parcel.parcelStatusName || "—"}
                          </span>
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-gray-500 uppercase">
                            Sender
                          </p>
                          <p className="font-semibold text-gray-900">
                            {parcel.senderName || "—"}
                          </p>
                          <p className="text-sm text-gray-600 truncate">
                            {parcel.senderEmail || "—"}
                          </p>
                          <p className="text-sm text-gray-600">
                            {parcel.senderPhone || "—"}
                          </p>
                          {parcel.senderPostCode && (
                            <p className="text-xs text-gray-500 mt-0.5">
                              {parcel.senderPostCode}
                            </p>
                          )}
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-gray-500 uppercase">
                            Receiver
                          </p>
                          <p className="font-semibold text-gray-900">
                            {parcel.receiverName || "—"}
                          </p>
                          <p className="text-sm text-gray-600 truncate">
                            {parcel.receiverEmail || "—"}
                          </p>
                          <p className="text-sm text-gray-600">
                            {parcel.receiverPhone || "—"}
                          </p>
                          {parcel.receiverPostCode && (
                            <p className="text-xs text-gray-500 mt-0.5">
                              {parcel.receiverPostCode}
                            </p>
                          )}
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-gray-500 uppercase">
                            Summary
                          </p>
                          <p className="text-sm text-gray-700">
                            {details.length}{" "}
                            {details.length === 1 ? "item" : "items"}
                          </p>
                          {paymentStatuses.length > 0 && (
                            <p className="text-sm font-medium text-gray-800 mt-1">
                              Payment: {paymentStatuses.join(", ")}
                            </p>
                          )}
                          {details[0]?.routeName && (
                            <p className="text-sm text-gray-700 mt-0.5">
                              {details[0].routeName}
                            </p>
                          )}
                          {totalSubtotal > 0 && (
                            <p className="text-sm font-semibold text-orange-600 mt-1">
                              {formatCurrency(totalSubtotal, slipSymbol || "GBP")}
                              {details.length > 1 && (
                                <span className="font-normal text-gray-600">
                                  {" "}
                                  total
                                </span>
                              )}
                            </p>
                          )}
                          {stripeCardLabel && (
                            <p className="text-xs text-gray-600 mt-1 truncate">
                              {stripeCardLabel}
                            </p>
                          )}
                        </div>
                      </div>
                    </button>
                    <div className="shrink-0 flex sm:flex-col border-t sm:border-t-0 sm:border-l border-[#e8d5c8] p-3 sm:p-4 justify-center">
                      <button
                        type="button"
                        onClick={() => openPaymentSlip(parcel)}
                        disabled={slipLoading && slipParcel?.parcelNo === parcel.parcelNo}
                        className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-orange-500 text-white text-sm font-semibold hover:bg-orange-600 disabled:opacity-60 whitespace-nowrap"
                      >
                        <FaFileInvoice />
                        {slipLoading && slipParcel?.parcelNo === parcel.parcelNo
                          ? "Loading..."
                          : "Payment slip"}
                      </button>
                    </div>
                  </div>

                  {isOpen && (
                    <div className="px-4 pb-4 sm:px-5 border-t border-[#e8d5c8] bg-[#fceee5]">
                      {(parcel.stripePaymentIntentId ||
                        parcel.stripeChargeId ||
                        stripeCardLabel) && (
                        <div className="mt-4 rounded-lg border border-[#e8d5c8] bg-white p-4">
                          <p className="text-xs font-semibold text-gray-500 uppercase mb-3">
                            Stripe / payment
                          </p>
                          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
                            {parcel.stripePaymentIntentId && (
                              <>
                                <dt className="text-gray-500">Payment intent</dt>
                                <dd className="font-mono text-xs break-all text-gray-900">
                                  {parcel.stripePaymentIntentId}
                                </dd>
                              </>
                            )}
                            {parcel.stripePaymentMethod && (
                              <>
                                <dt className="text-gray-500">Payment method</dt>
                                <dd className="font-mono text-xs break-all text-gray-900">
                                  {parcel.stripePaymentMethod}
                                </dd>
                              </>
                            )}
                            {parcel.stripeChargeId && (
                              <>
                                <dt className="text-gray-500">Charge</dt>
                                <dd className="font-mono text-xs break-all text-gray-900">
                                  {parcel.stripeChargeId}
                                </dd>
                              </>
                            )}
                            {stripeCardLabel && (
                              <>
                                <dt className="text-gray-500">Card</dt>
                                <dd className="font-medium text-gray-900">
                                  {stripeCardLabel}
                                </dd>
                              </>
                            )}
                            {details[0]?.paymentStatus && (
                              <>
                                <dt className="text-gray-500">Line payment status</dt>
                                <dd className="font-medium text-gray-900">
                                  {details[0].paymentStatus}
                                </dd>
                              </>
                            )}
                          </dl>
                        </div>
                      )}
                      <div className="flex justify-end py-2 sm:hidden">
                        <button
                          type="button"
                          onClick={() => openPaymentSlip(parcel)}
                          disabled={
                            slipLoading && slipParcel?.parcelNo === parcel.parcelNo
                          }
                          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-orange-500 text-white text-sm font-semibold"
                        >
                          <FaFileInvoice />
                          Payment slip
                        </button>
                      </div>
                      {details.length === 0 ? (
                        <p className="py-4 text-sm text-gray-600 text-center">
                          No line items for this parcel.
                        </p>
                      ) : (
                        <div className="overflow-x-auto mt-3 rounded-lg border border-[#e8d5c8]">
                          <table className="w-full min-w-[1200px] text-sm">
                            <thead>
                              <tr className="bg-[#f5cfb5] text-center">
                                <th className="py-3 px-2">#</th>
                                <th className="py-3 px-2">Created</th>
                                <th className="py-3 px-2">Route</th>
                                <th className="py-3 px-2">Cargo</th>
                                <th className="py-3 px-2">Service</th>
                                <th className="py-3 px-2">Weight</th>
                                <th className="py-3 px-2">Dimension</th>
                                <th className="py-3 px-2">Dim. cost</th>
                                <th className="py-3 px-2">Item</th>
                                <th className="py-3 px-2">Notes</th>
                                <th className="py-3 px-2">Payment</th>
                                <th className="py-3 px-2">Sub total</th>
                                <th className="py-3 px-2">Commission</th>
                                <th className="py-3 px-2">Agent</th>
                              </tr>
                            </thead>
                            <tbody>
                              {details.map((row, idx) => (
                                <tr
                                  key={`${parcel.parcelNo}-${idx}`}
                                  className={
                                    idx % 2 === 0 ? "bg-white" : "bg-[#fcf4ef]"
                                  }
                                >
                                  <td className="py-2 px-2 text-center font-medium">
                                    {globalIndex}.{idx + 1}
                                  </td>
                                  <td className="py-2 px-2 text-center whitespace-nowrap">
                                    {formatDateTime(row.parcelCreationDate)}
                                  </td>
                                  <td className="py-2 px-2 text-center">
                                    {row.routeName || "—"}
                                  </td>
                                  <td className="py-2 px-2 text-center">
                                    {row.cargoTypeName || "—"}
                                  </td>
                                  <td className="py-2 px-2 text-center">
                                    {row.shippingServiceName || "—"}
                                  </td>
                                  <td className="py-2 px-2 text-center">
                                    {row.weightDescription || "—"}
                                  </td>
                                  <td className="py-2 px-2 text-center">
                                    {row.dimensionName || "—"}
                                  </td>
                                  <td className="py-2 px-2 text-center font-medium">
                                    {formatCurrency(row.dimensionCost, row.symbol || "GBP")}
                                  </td>
                                  <td className="py-2 px-2 text-center max-w-[120px]">
                                    <span className="line-clamp-2">
                                      {row.uniqItemDescription || "—"}
                                    </span>
                                  </td>
                                  <td className="py-2 px-2 text-center max-w-[120px]">
                                    <span className="line-clamp-2">
                                      {row.parcelAdditionalInfo || "—"}
                                    </span>
                                  </td>
                                  <td className="py-2 px-2 text-center">
                                    {row.paymentStatus || "—"}
                                  </td>
                                  <td className="py-2 px-2 text-center font-semibold">
                                    {formatCurrency(row.subTotal, row.symbol || "GBP")}
                                  </td>
                                  <td className="py-2 px-2 text-center whitespace-nowrap">
                                    {row.fixedCommisionAmount != null ||
                                    row.agentCommisionPayable != null ? (
                                      <>
                                        {formatCurrency(
                                          row.fixedCommisionAmount,
                                          row.symbol || "GBP",
                                        )}
                                        {row.agentCommisionPayable != null && (
                                          <span className="block text-xs text-gray-600">
                                            payable{" "}
                                            {formatCurrency(
                                              row.agentCommisionPayable,
                                              row.symbol || "GBP",
                                            )}
                                          </span>
                                        )}
                                      </>
                                    ) : (
                                      "—"
                                    )}
                                  </td>
                                  <td className="py-2 px-2 text-center">
                                    {row.agentName || "—"}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {filteredParcels.length > 0 && (
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-6">
            <p className="text-sm text-gray-600">
              Showing {(currentPage - 1) * ITEMS_PER_PAGE + 1}–
              {Math.min(currentPage * ITEMS_PER_PAGE, filteredParcels.length)} of{" "}
              {filteredParcels.length}
              {filteredParcels.length !== parcels.length &&
                ` (filtered from ${parcels.length})`}
            </p>
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage <= 1 || loading}
                className="px-4 py-2 rounded-lg border border-gray-300 bg-white font-medium disabled:opacity-50 hover:bg-gray-50"
              >
                Previous
              </button>
              {pageNumbers.map((page) => (
                <button
                  key={page}
                  type="button"
                  onClick={() => setCurrentPage(page)}
                  className={`min-w-[40px] px-3 py-2 rounded-lg border text-sm font-medium ${
                    currentPage === page
                      ? "bg-[#6D4C41] text-white border-[#6D4C41]"
                      : "bg-white border-gray-300 hover:bg-gray-50"
                  }`}
                >
                  {page}
                </button>
              ))}
              <button
                type="button"
                onClick={() =>
                  setCurrentPage((p) => Math.min(totalPages, p + 1))
                }
                disabled={currentPage >= totalPages || loading}
                className="px-4 py-2 rounded-lg border border-gray-300 bg-white font-medium disabled:opacity-50 hover:bg-gray-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </Box>

      {(slipParcel || slipPaymentData) && (
        <div
          className="fixed inset-0 z-[100] flex items-start justify-center overflow-y-auto bg-black/60 p-4 sm:p-8"
          role="dialog"
          aria-modal="true"
          aria-labelledby="admin-payment-slip-title"
        >
          <div className="relative w-full max-w-5xl my-4">
            <div className="sticky top-0 z-10 flex items-center justify-between gap-4 rounded-t-xl bg-[#6D4C41] px-4 py-3 text-white shadow-md">
              <div>
                <h2 id="admin-payment-slip-title" className="text-lg font-bold">
                  Payment slip — Parcel #{slipParcel?.parcelNo}
                </h2>
                <p className="text-sm text-white/80">
                  Same layout as customer booking receipt
                </p>
              </div>
              <button
                type="button"
                onClick={closePaymentSlip}
                className="p-2 rounded-lg hover:bg-white/10 transition-colors"
                aria-label="Close payment slip"
              >
                <IoClose className="text-2xl" />
              </button>
            </div>

            <div className="bg-gray-100 rounded-b-xl shadow-2xl overflow-hidden">
              {slipLoading ? (
                <div className="flex justify-center items-center py-24">
                  <p className="text-lg font-medium text-gray-600">
                    Preparing payment slip...
                  </p>
                </div>
              ) : slipPaymentData ? (
                <div className="max-h-[calc(100vh-8rem)] overflow-y-auto p-2 sm:p-4">
                  <PaymentSlip
                    paymentDataOverride={slipPaymentData}
                    disableAutoUpload
                    embedded
                  />
                </div>
              ) : null}
            </div>
          </div>
        </div>
      )}
    </SetupContainer>
  );
};

export default AdminBookings;
